import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { userService, brandService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '../components/Spinner';

// ── Multi-select dropdown ───────────────────────────────────────────────────
function MultiSelectDropdown({ options, selected, onChange, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const selectedLabels = options.filter(o => selected.includes(o.value)).map(o => o.label);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input flex items-center justify-between w-full text-left cursor-pointer"
      >
        <div className="flex flex-wrap gap-1 min-h-[1.25rem]">
          {selectedLabels.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            selectedLabels.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded">
                {l}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    const opt = options.find(o => o.label === l);
                    if (opt) toggle(opt.value);
                  }}
                  className="hover:text-primary-900 cursor-pointer"
                >×</span>
              </span>
            ))
          )}
        </div>
        <ChevronDownIcon className={`h-4 w-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No options</div>
          ) : (
            options.map(opt => {
              const isSelected = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(opt.value)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="flex-1">{opt.label}</span>
                  {isSelected && <CheckCircleIcon className="h-4 w-4 text-primary-500" />}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const PASSWORD_RULES = [
  { id: 'length',    label: 'At least 6 characters',     test: (p) => p.length >= 6 },
  { id: 'uppercase', label: 'One uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { id: 'number',    label: 'One number (0–9)',           test: (p) => /\d/.test(p) },
];

export const AddUser = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission, user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const role = searchParams.get('role') || 'sales_executive';

  // Fetch hierarchy users for parent selection (dealers under current user)
  const { data: hierarchyData } = useQuery(
    'hierarchyUsers',
    () => userService.getHierarchy(),
    { staleTime: 5 * 60 * 1000 }
  );
  const hierarchyUsers = hierarchyData?.data?.data?.users || hierarchyData?.data?.users || [];

  // Fetch brands list for dealer brand selection
  const { data: brandsData } = useQuery(
    'brandsForUserForm',
    () => brandService.getBrands(),
    { staleTime: 10 * 60 * 1000 }
  );
  const brandsList = brandsData?.data?.data?.brands || brandsData?.data?.brands || [];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role,
    parentId: '',
    status: 'active',
    brands: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const createUserMutation = useMutation(userService.createUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      navigate('/users');
    },
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (field === 'password') setPasswordTouched(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required';


    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    // Required and unique like email — uniqueness is checked by the server
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[\d\s\-()]+$/.test(formData.phone.trim()) || formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Enter a valid phone number (at least 10 digits)';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const failedRules = PASSWORD_RULES.filter(r => !r.test(formData.password));
      if (failedRules.length > 0) {
        newErrors.password = `Password must meet all requirements below`;
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.role === 'sales_executive' && !formData.parentId && currentUser.role !== 'dealer') {
      newErrors.parentId = 'Please select a dealer for this sales executive';
    }

    // Super admin creating a dealer must pick an admin — dealers can't sit directly under super_admin
    if (formData.role === 'dealer' && currentUser.role === 'super_admin' && !formData.parentId) {
      newErrors.parentId = 'Please select an admin for this dealer';
    }

    if (formData.role === 'dealer' && (!formData.brands || formData.brands.length === 0)) {
      newErrors.brands = 'Please select at least one brand for this dealer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasPermission('users', 'create')) {
      setErrors({ submit: 'You do not have permission to create users.' });
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { confirmPassword, ...userData } = formData;
      await createUserMutation.mutateAsync(userData);
    } catch (error) {
      const serverMessage = error.response?.data?.message;
      const serverErrors = error.response?.data?.errors;

      if (serverErrors?.length) {
        // Map express-validator field errors back to form fields
        const mapped = {};
        serverErrors.forEach(({ path, msg }) => {
          if (path) mapped[path] = msg;
        });
        if (Object.keys(mapped).length) {
          setErrors(mapped);
        } else {
          setErrors({ submit: serverMessage || 'Validation failed. Please check your inputs.' });
        }
      } else {
        setErrors({ submit: serverMessage || 'Failed to create user. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const CREATABLE_ROLES = {
    super_admin: [
      { value: 'admin', label: 'Admin' },
      { value: 'dealer', label: 'Dealer' },
      { value: 'sales_executive', label: 'Sales Executive' },
    ],
    admin: [
      { value: 'dealer', label: 'Dealer' },
      { value: 'sales_executive', label: 'Sales Executive' },
    ],
    dealer: [
      { value: 'sales_executive', label: 'Sales Executive' },
    ],
  };
  const availableRoles = CREATABLE_ROLES[currentUser.role] || [];

  if (availableRoles.length === 0) {
    return (
      <div className="text-center">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
        <p className="mt-1 text-gray-600">You don't have permission to create users.</p>
        <button onClick={() => navigate('/users')} className="mt-4 btn-outline-secondary">
          Back to Users
        </button>
      </div>
    );
  }

  const passwordRulesMet = PASSWORD_RULES.map(r => ({ ...r, passed: r.test(formData.password) }));
  const allRulesPassed = passwordRulesMet.every(r => r.passed);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/users')} className="p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add New User</h1>
            <p className="mt-2 text-gray-600">Create a new user account</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6" noValidate>

          {/* Server / general error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded flex items-start gap-2">
              <XCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>{errors.submit}</span>
            </div>
          )}

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">

              {/* First Name */}
              <div>
                <label className="label">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className={`input ${errors.firstName ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <XCircleIcon className="h-4 w-4" />{errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="label">
                  Last Name <span className="text-xs font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className={`input ${errors.lastName ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Doe"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <XCircleIcon className="h-4 w-4" />{errors.lastName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="label">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`input ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="john@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <XCircleIcon className="h-4 w-4" />{errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="label">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className={`input ${errors.phone ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="e.g. 9876543210"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <XCircleIcon className="h-4 w-4" />{errors.phone}
                  </p>
                )}
              </div>

            </div>
          </div>

          {/* Account Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">

              {/* Password */}
              <div className="sm:col-span-2 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="label">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      className={`input pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword
                        ? <EyeSlashIcon className="h-5 w-5" />
                        : <EyeIcon className="h-5 w-5" />
                      }
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <XCircleIcon className="h-4 w-4" />{errors.password}
                    </p>
                  )}

                  {/* Password rules checklist */}
                  {(passwordTouched || errors.password) && (
                    <ul className="mt-2 space-y-1">
                      {passwordRulesMet.map(rule => (
                        <li key={rule.id} className={`flex items-center gap-1.5 text-sm ${rule.passed ? 'text-green-600' : 'text-gray-400'}`}>
                          {rule.passed
                            ? <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                            : <XCircleIcon className="h-4 w-4 flex-shrink-0" />
                          }
                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="label">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      className={`input pr-10 ${errors.confirmPassword ? 'border-red-400 focus:ring-red-400' : ''}`}
                      placeholder="Re-enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(p => !p)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword
                        ? <EyeSlashIcon className="h-5 w-5" />
                        : <EyeIcon className="h-5 w-5" />
                      }
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <XCircleIcon className="h-4 w-4" />{errors.confirmPassword}
                    </p>
                  )}
                  {/* Match indicator */}
                  {formData.confirmPassword && formData.password && !errors.confirmPassword && allRulesPassed && (
                    <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                      <CheckCircleIcon className="h-4 w-4" />Passwords match
                    </p>
                  )}
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="label">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className={`input ${errors.role ? 'border-red-400 focus:ring-red-400' : ''}`}
                >
                  {availableRoles.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <XCircleIcon className="h-4 w-4" />{errors.role}
                  </p>
                )}
              </div>

              {/* Assign Under (parent) — show when creating dealer or sales_executive, but not for dealers (they are always the parent) */}
              {(formData.role === 'dealer' || formData.role === 'sales_executive') && hierarchyUsers.length > 0 && currentUser.role !== 'dealer' && (
                <div>
                  <label className="label">
                    Assign Under
                    {((formData.role === 'sales_executive' && currentUser.role !== 'dealer') ||
                      (formData.role === 'dealer' && currentUser.role === 'super_admin')) && (
                      <span className="text-red-500"> *</span>
                    )}
                  </label>
                  <select
                    value={formData.parentId}
                    onChange={(e) => handleChange('parentId', e.target.value)}
                    className="input"
                  >
                    <option value="">
                      {formData.role === 'sales_executive'
                        ? 'Select Dealer'
                        : (formData.role === 'dealer' && currentUser.role === 'super_admin')
                          ? 'Select Admin'
                          : 'Myself (default)'}
                    </option>
                    {hierarchyUsers
                      .filter(u => {
                        // Never show current user — "Myself (default)" already covers that
                        if (u._id === currentUser._id || u._id === currentUser.id) return false;
                        // For sales_executive, show only dealers as possible parents
                        if (formData.role === 'sales_executive') return u.role === 'dealer';
                        // For dealer, show admins as possible parents
                        if (formData.role === 'dealer') return u.role === 'admin';
                        return false;
                      })
                      .map(u => (
                        <option key={u._id} value={u._id}>
                          {u.firstName} {u.lastName} ({u.role === 'admin' ? 'Admin' : u.role === 'dealer' ? 'Dealer' : 'Sales Executive'})
                        </option>
                      ))}
                  </select>
                  {errors.parentId && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <XCircleIcon className="h-4 w-4" />{errors.parentId}
                    </p>
                  )}
                </div>
              )}

              {/* Brands — only when creating a dealer */}
              {formData.role === 'dealer' && (
                <div className="sm:col-span-2">
                  <label className="label">
                    Brands <span className="text-red-500">*</span>
                  </label>
                  {brandsList.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No brands configured yet. Add brands from Settings → Brands & Models.</p>
                  ) : (
                    <MultiSelectDropdown
                      options={brandsList.filter(b => b.isActive !== false).map(b => ({ value: b._id, label: b.name }))}
                      selected={formData.brands}
                      onChange={(next) => handleChange('brands', next)}
                      placeholder="Select brands…"
                    />
                  )}
                  {errors.brands ? (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <XCircleIcon className="h-4 w-4" />{errors.brands}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">Select the brands this dealer represents.</p>
                  )}
                </div>
              )}

              {/* Status */}
              <div>
                <label className="label">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/users')}
              className="btn-outline-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <><Spinner size="sm" className="mr-2" />Creating...</>
              ) : (
                'Create User'
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};
